import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { Modal, View, TouchableOpacity, KeyboardAvoidingView, StyleSheet, Platform, ScrollView, InteractionManager, useWindowDimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { X } from 'lucide-react-native';
import { OTPLoginFlow, OTPLoginFlowHandle } from '@/components/OTPLoginFlow';
import { useAuth } from '@/hooks/useAuth';
import { appStabilityGuard } from '@/utils/appStabilityGuard';

const GESTURE_RECOVERY_DELAY_IOS = 700;
const GESTURE_RECOVERY_DELAY_ANDROID = 400;

type AuthModalType = 'signIn' | 'signUp' | null;
type AuthModalVariant = 'fullscreen' | 'inline';

interface AuthSuccessCallback {
  onSuccess?: () => void;
  onDismiss?: () => void;
  contextMessage?: string;
  variant?: AuthModalVariant;
  showSocialLogin?: boolean;
}

interface AuthModalsContextValue {
  showSignInModal: (callbacks?: AuthSuccessCallback) => void;
  showSignUpModal: (callbacks?: AuthSuccessCallback) => void;
  hideAuthModals: () => void;
  isAuthModalVisible: boolean;
  currentModalType: AuthModalType;
}

const AuthModalsContext = createContext<AuthModalsContextValue | null>(null);

export function useAuthModals() {
  const context = useContext(AuthModalsContext);
  if (!context) {
    throw new Error('useAuthModals must be used within AuthModalsProvider');
  }
  return context;
}

interface AuthModalsProviderProps {
  children: React.ReactNode;
}

export function AuthModalsProvider({ children }: AuthModalsProviderProps) {
  const { authState } = useAuth();
  const insets = useSafeAreaInsets();
  const { width: windowWidth } = useWindowDimensions();
  const isDesktopWeb = Platform.OS === 'web' && windowWidth >= 768;

  const [currentModal, setCurrentModal] = useState<AuthModalType>(null);
  const [contextMessage, setContextMessage] = useState<string | undefined>(undefined);
  const [variant, setVariant] = useState<AuthModalVariant>('fullscreen');
  const [showSocialLogin, setShowSocialLogin] = useState<boolean>(true);
  const callbacksRef = useRef<AuthSuccessCallback>({});
  const previousUserRef = useRef<string | null>(null);
  const recoveryInProgressRef = useRef<boolean>(false);
  const callbackExecutedRef = useRef<boolean>(false);
  const otpLoginRef = useRef<OTPLoginFlowHandle>(null);

  useEffect(() => {
    const currentUserId = authState.user?.id || null;
    const wasLoggedOut = previousUserRef.current === null;
    const isNowLoggedIn = currentUserId !== null;

    if (wasLoggedOut && isNowLoggedIn && currentModal !== null) {
      const globalRecoveryInProgress = appStabilityGuard.isRecoveryInProgress();
      console.log('[AUTH-MODALS] Fallback: User logged in while modal open, checking flags...');
      console.log('[AUTH-MODALS] Fallback: localRecovery:', recoveryInProgressRef.current, 'globalRecovery:', globalRecoveryInProgress, 'callbackExecuted:', callbackExecutedRef.current);

      if (recoveryInProgressRef.current || globalRecoveryInProgress) {
        console.log('[AUTH-MODALS] Fallback: BLOCKED - recovery sequence already in progress (local or global)');
        previousUserRef.current = currentUserId;
        return;
      }

      if (callbackExecutedRef.current) {
        console.log('[AUTH-MODALS] Fallback: BLOCKED - callback already executed');
        previousUserRef.current = currentUserId;
        return;
      }

      console.log('[AUTH-MODALS] Fallback: Scheduling modal close (recovery not in progress)');
      setTimeout(() => {
        const globalRecoveryDeferred = appStabilityGuard.isRecoveryInProgress();
        if (recoveryInProgressRef.current || globalRecoveryDeferred) {
          console.log('[AUTH-MODALS] Fallback: BLOCKED (deferred) - recovery started during timeout (local:', recoveryInProgressRef.current, 'global:', globalRecoveryDeferred, ')');
          return;
        }

        if (callbackExecutedRef.current) {
          console.log('[AUTH-MODALS] Fallback: BLOCKED (deferred) - callback was executed during timeout');
          return;
        }

        setCurrentModal(prev => {
          if (prev !== null) {
            console.log('[AUTH-MODALS] Fallback: Forcing modal close and executing callback');
            callbackExecutedRef.current = true;
            callbacksRef.current.onSuccess?.();
            callbacksRef.current = {};
            appStabilityGuard.setAuthModalClosed(true);
            return null;
          }
          return prev;
        });
      }, 100);
    }

    previousUserRef.current = currentUserId;
  }, [authState.user?.id, currentModal]);

  const showSignInModal = useCallback((callbacks?: AuthSuccessCallback) => {
    console.log('[AUTH-MODALS] Opening OTP auth modal (signIn) - notifying stability guard');
    appStabilityGuard.setAuthModalClosed(false);
    callbacksRef.current = callbacks || {};
    recoveryInProgressRef.current = false;
    callbackExecutedRef.current = false;
    setContextMessage(callbacks?.contextMessage);
    setVariant(callbacks?.variant ?? 'fullscreen');
    setShowSocialLogin(callbacks?.showSocialLogin ?? true);
    setCurrentModal('signIn');
  }, []);

  const showSignUpModal = useCallback((callbacks?: AuthSuccessCallback) => {
    console.log('[AUTH-MODALS] Opening OTP auth modal (signUp) - notifying stability guard');
    appStabilityGuard.setAuthModalClosed(false);
    callbacksRef.current = callbacks || {};
    recoveryInProgressRef.current = false;
    callbackExecutedRef.current = false;
    setContextMessage(callbacks?.contextMessage);
    setVariant(callbacks?.variant ?? 'fullscreen');
    setShowSocialLogin(callbacks?.showSocialLogin ?? true);
    setCurrentModal('signUp');
  }, []);

  const hideAuthModals = useCallback(() => {
    setCurrentModal(null);
    setContextMessage(undefined);
    callbacksRef.current = {};
    if (recoveryInProgressRef.current) {
      recoveryInProgressRef.current = false;
      appStabilityGuard.setRecoveryInProgress(false);
      console.log('[AUTH-MODALS] Recovery flags reset on hideAuthModals');
    }
  }, []);

  const handleAuthSuccess = useCallback(() => {
    console.log('[AUTH-MODALS] OTP auth successful - starting extended recovery sequence');

    if (callbackExecutedRef.current) {
      console.log('[AUTH-MODALS] Auth callback already executed, skipping');
      return;
    }

    recoveryInProgressRef.current = true;
    appStabilityGuard.setRecoveryInProgress(true);
    console.log('[AUTH-MODALS] Recovery flags set: recoveryInProgress=true');

    const recoveryDelay = Platform.OS === 'ios' ? GESTURE_RECOVERY_DELAY_IOS : GESTURE_RECOVERY_DELAY_ANDROID;

    InteractionManager.runAfterInteractions(() => {
      console.log(`[AUTH-MODALS] InteractionManager complete, starting ${recoveryDelay}ms gesture recovery delay`);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          console.log('[AUTH-MODALS] Double RAF complete, starting timer');

          setTimeout(() => {
            if (callbackExecutedRef.current) {
              console.log('[AUTH-MODALS] Callback already executed during recovery, aborting');
              recoveryInProgressRef.current = false;
              appStabilityGuard.setRecoveryInProgress(false);
              return;
            }

            console.log('[AUTH-MODALS] Closing modal after full recovery sequence');
            setCurrentModal(null);
            appStabilityGuard.setAuthModalClosed(true);

            const callbackDelay = Platform.OS === 'ios' ? 200 : 100;
            setTimeout(() => {
              if (callbackExecutedRef.current) {
                console.log('[AUTH-MODALS] Callback already executed, skipping final callback');
                recoveryInProgressRef.current = false;
                appStabilityGuard.setRecoveryInProgress(false);
                return;
              }

              console.log('[AUTH-MODALS] Executing success callback after additional stability delay');
              callbackExecutedRef.current = true;
              callbacksRef.current.onSuccess?.();
              callbacksRef.current = {};

              recoveryInProgressRef.current = false;
              appStabilityGuard.setRecoveryInProgress(false);
              console.log('[AUTH-MODALS] Recovery complete, flags reset');
            }, callbackDelay);
          }, recoveryDelay);
        });
      });
    });
  }, []);

  const handleDismiss = useCallback(() => {
    console.log('[AUTH-MODALS] Modal dismissed - notifying stability guard');
    setCurrentModal(null);
    appStabilityGuard.setAuthModalClosed(true);
    if (recoveryInProgressRef.current) {
      recoveryInProgressRef.current = false;
      appStabilityGuard.setRecoveryInProgress(false);
      console.log('[AUTH-MODALS] Recovery flags reset on dismiss');
    }
    callbacksRef.current.onDismiss?.();
    callbacksRef.current = {};
  }, []);

  const contextValue: AuthModalsContextValue = {
    showSignInModal,
    showSignUpModal,
    hideAuthModals,
    isAuthModalVisible: currentModal !== null,
    currentModalType: currentModal,
  };

  const renderFullscreenModal = () => (
    <View style={[styles.fullScreenModal, { paddingTop: insets.top }]}>
      <View style={styles.fullScreenModalHeader}>
        <View style={styles.headerSpacer} />
        <TouchableOpacity
          style={styles.fullScreenModalCloseButton}
          onPress={() => {
            if (otpLoginRef.current) {
              otpLoginRef.current.requestClose();
            } else {
              handleDismiss();
            }
          }}
        >
          <X size={26} color="#ffffff" />
        </TouchableOpacity>
      </View>
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.fullScreenModalContent}
      >
        <ScrollView
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
          contentContainerStyle={styles.scrollViewContent}
        >
          <View style={[styles.fullScreenModalFormContainer, isDesktopWeb && styles.fullScreenModalFormContainerDesktop]}>
            <OTPLoginFlow
              ref={otpLoginRef}
              onSuccessCallback={handleAuthSuccess}
              onClose={handleDismiss}
              showSocialLogin={showSocialLogin}
              contextMessage={contextMessage}
            />
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );

  const renderInlineModal = () => (
    <View style={styles.inlineOverlay}>
      <View style={styles.inlineCard}>
        <View style={styles.inlineHeader}>
          <View style={styles.headerSpacer} />
          <TouchableOpacity
            onPress={() => {
              if (otpLoginRef.current) {
                otpLoginRef.current.requestClose();
              } else {
                handleDismiss();
              }
            }}
          >
            <X size={24} color="#ffffff" />
          </TouchableOpacity>
        </View>
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
          style={styles.inlineContent}
        >
          <ScrollView
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator={false}
            contentContainerStyle={styles.inlineScrollContent}
          >
            <OTPLoginFlow
              ref={otpLoginRef}
              onSuccessCallback={handleAuthSuccess}
              onClose={handleDismiss}
              showSocialLogin={showSocialLogin}
              contextMessage={contextMessage}
            />
          </ScrollView>
        </KeyboardAvoidingView>
      </View>
    </View>
  );

  return (
    <AuthModalsContext.Provider value={contextValue}>
      {children}

      <Modal
        visible={currentModal !== null}
        animationType={variant === 'inline' ? 'fade' : 'slide'}
        transparent={variant === 'inline'}
        statusBarTranslucent
        onRequestClose={handleDismiss}
      >
        {variant === 'inline' ? renderInlineModal() : renderFullscreenModal()}
      </Modal>
    </AuthModalsContext.Provider>
  );
}

const styles = StyleSheet.create({
  fullScreenModal: {
    flex: 1,
    backgroundColor: '#1a1a1a',
  },
  fullScreenModalHeader: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingVertical: 18,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.1)',
  },
  headerSpacer: {
    flex: 1,
  },
  fullScreenModalCloseButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  fullScreenModalContent: {
    flex: 1,
  },
  fullScreenModalFormContainer: {
    flex: 1,
    paddingTop: 16,
  },
  fullScreenModalFormContainerDesktop: {
    maxWidth: '50%',
    alignSelf: 'center',
    width: '100%',
  },
  scrollViewContent: {
    flexGrow: 1,
    paddingBottom: 150,
  },
  inlineOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  inlineCard: {
    backgroundColor: '#1a1a1a',
    borderRadius: 16,
    width: '100%',
    maxWidth: 440,
    maxHeight: '90%',
    overflow: 'hidden',
  },
  inlineHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: '#333',
  },
  inlineContent: {
    flexShrink: 1,
  },
  inlineScrollContent: {
    flexGrow: 1,
    paddingBottom: 24,
  },
});
